import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { unifiedCallService } from '../services/unifiedCallService';

// Async thunks for unified call operations
export const createCall = createAsyncThunk(
  'unifiedCall/createCall',
  async (callData, { rejectWithValue }) => {
    try {
      const response = await unifiedCallService.createCall(callData);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCall = createAsyncThunk(
  'unifiedCall/fetchCall',
  async (callId, { rejectWithValue }) => {
    try {
      const response = await unifiedCallService.getCall(callId);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCall = createAsyncThunk(
  'unifiedCall/updateCall',
  async ({ callId, data }, { rejectWithValue }) => {
    try {
      const response = await unifiedCallService.updateCall(callId, data);
      return response
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const endCall = createAsyncThunk(
  'unifiedCall/endCall',
  async (callId, { rejectWithValue }) => {
    try {
      const response = await unifiedCallService.endCall(callId);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCalls = createAsyncThunk(
  'unifiedCall/fetchCalls',
  async (params, { rejectWithValue }) => {
    try {
      const response = await unifiedCallService.getCalls(params);
      return response
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const unifiedCallSlice = createSlice({
  name: 'unifiedCall',
  initialState: {
    calls: [],
    activeCall: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setActiveCall: (state, action) => {
      state.activeCall = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create call
      .addCase(createCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCall.fulfilled, (state, action) => {
        state.loading = false;
        state.calls.push(action.payload);
        state.activeCall = action.payload;
      })
      .addCase(createCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch call
      .addCase(fetchCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCall.fulfilled, (state, action) => {
        state.loading = false;
        state.activeCall = action.payload;
      })
      .addCase(fetchCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update call
      .addCase(updateCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCall.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.calls.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.calls[index] = action.payload;
        }
        if (state.activeCall?.id === action.payload.id) {
          state.activeCall = action.payload;
        }
      })
      .addCase(updateCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // End call
      .addCase(endCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(endCall.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.calls.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.calls[index] = action.payload;
        }
        if (state.activeCall?.id === action.payload.id) {
          state.activeCall = action.payload;
        }
      })
      .addCase(endCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch calls
      .addCase(fetchCalls.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalls.fulfilled, (state, action) => {
        state.loading = false;
        state.calls = action.payload.calls || action.payload;
      })
      .addCase(fetchCalls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setActiveCall } = unifiedCallSlice.actions;
export default unifiedCallSlice.reducer;